import { useCallback, useEffect, useState } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { QueryClientProvider } from '@tanstack/react-query'
import { dashboardQueryClient } from 'src/v3/manage/dashboard/query-client'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Typography from '@mui/material/Typography'
import { useTheme } from '@mui/material/styles'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import {
  fetchManageEventCapacity,
  selectManageEventById,
  selectManageEventCapacity,
  selectManageEventCapacityStatus,
} from '../manage-events.slice'
import InviteUsersDialog from '../components/invite-users-dialog'
import SessionScopePicker from '../components/session-scope-picker'
import RsvpStatCards from '../components/rsvp-stat-cards'
import { LICENSE_TYPE_COLORS } from '../utils/constants'
import { formatSessionDate } from '../utils/session-time'
import { useSessionScope } from '../hooks/use-session-scope'
import type { EventLicenseCapacityRow } from '../types'

dayjs.extend(utc)

type Props = { eventID: string }

let LICENSE_ORDER = ['EMT', 'AEMT', 'Paramedic', 'Registered Nurse', 'Other']

let BASE_COL_HEADERS = [
  'License type',
  'Pending RSVP',
  'Accepted',
  'Waitlisted',
  'Declined',
  'Attended',
]
let SINGLE_COL_HEADERS = [...BASE_COL_HEADERS, 'User cap', 'Seats remaining']

let BASE_COL_TEMPLATE = '1.4fr 1fr 1fr 1fr 1fr 1fr'
let SINGLE_COL_TEMPLATE = '1.4fr 1fr 1fr 1fr 1fr 1fr 1fr 1fr'

let DASH = '–'

let formatNum = (n: number) => n.toLocaleString('en-US')

// ─── Row renderer ─────────────────────────────────────────────────────────────

type CapRowProps = {
  row: EventLicenseCapacityRow
  isSingleMode: boolean
  colTemplate: string
}

let CapacityRow = ({ row, isSingleMode, colTemplate }: CapRowProps) => {
  let theme = useTheme()
  let licColor = LICENSE_TYPE_COLORS[row.licenseType] ?? '#72CCFF'

  let pendingColor =
    row.pending > 0
      ? (theme.palette.analyticsColors?.recordedCourses ?? '#B39DDB')
      : 'rgba(255,255,255,.85)'
  let acceptedColor =
    row.accepted > 0 ? theme.palette.info.main : 'rgba(255,255,255,.85)'
  let waitlistedColor =
    row.waitlisted > 0 ? theme.palette.warning.main : 'rgba(255,255,255,.85)'
  let declinedColor =
    row.declined > 0 ? theme.palette.error.main : 'rgba(255,255,255,.85)'
  let attendedColor =
    row.attended > 0 ? theme.palette.success.main : 'rgba(255,255,255,.85)'

  let isFull = isSingleMode && row.remaining === 0 && row.capacity != null
  let remainingColor = isFull ? '#ffa726' : 'rgba(255,255,255,.85)'

  let capDisplay = row.capacity == null ? DASH : formatNum(row.capacity)
  let remainingDisplay = row.remaining == null ? DASH : formatNum(row.remaining)

  return (
    <Box
      className="grid gap-3 items-center"
      sx={{
        gridTemplateColumns: colTemplate,
        padding: '14px 0',
        borderBottom: '1px solid rgba(255,255,255,.08)',
      }}
    >
      <Typography sx={{ color: licColor, fontSize: 15, fontWeight: 500 }}>
        {row.licenseType}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: 15, color: pendingColor }}
      >
        {formatNum(row.pending)}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: 15, color: acceptedColor }}
      >
        {formatNum(row.accepted)}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: 15, color: waitlistedColor }}
      >
        {formatNum(row.waitlisted)}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: 15, color: declinedColor }}
      >
        {formatNum(row.declined)}
      </Typography>
      <Typography
        className="text-center"
        sx={{ fontSize: 15, color: attendedColor }}
      >
        {formatNum(row.attended)}
      </Typography>
      {isSingleMode && (
        <Typography className="text-center" sx={{ fontSize: 15 }}>
          {capDisplay}
        </Typography>
      )}
      {isSingleMode && (
        <Typography
          className="text-center"
          sx={{ fontSize: 15, color: remainingColor }}
        >
          {remainingDisplay}
        </Typography>
      )}
    </Box>
  )
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

let CapacityBreakdownTab = ({ eventID }: Props) => {
  let dispatch = useAppDispatch()
  let capacity = useAppSelector(selectManageEventCapacity)
  let status = useAppSelector(selectManageEventCapacityStatus)

  let scopeState = useSessionScope()
  let { scope, range, singleSession } = scopeState

  let isSingleMode = scope === 'single'
  let isLoading = status === 'uninitialized' || status === 'pending'

  let colHeaders = isSingleMode ? SINGLE_COL_HEADERS : BASE_COL_HEADERS
  let colTemplate = isSingleMode ? SINGLE_COL_TEMPLATE : BASE_COL_TEMPLATE

  let event = useAppSelector(selectManageEventById(eventID))
  let [inviteOpen, setInviteOpen] = useState(false)

  let refetchCapacity = useCallback(() => {
    dispatch(
      fetchManageEventCapacity({
        eventID,
        scope,
        range,
        sessionID: singleSession?.id,
      }),
    )
  }, [scope, range, singleSession, eventID, dispatch])

  useEffect(() => {
    if (isSingleMode && !singleSession) return
    refetchCapacity()
  }, [isSingleMode, singleSession, refetchCapacity])

  let linkedServiceID = event?.linkedServiceID
  let canInvite =
    isSingleMode &&
    singleSession != null &&
    dayjs.utc(singleSession.ts).isAfter(dayjs.utc()) &&
    !!linkedServiceID

  let capTitle = isSingleMode
    ? singleSession
      ? singleSession.title
      : 'Single session'
    : scope === 'past'
      ? 'Past sessions'
      : 'Upcoming sessions'

  let capSub = (() => {
    if (isSingleMode) {
      return singleSession
        ? formatSessionDate(singleSession.ts, singleSession.timeZone)
        : 'Search all sessions and pick one to inspect.'
    }
    if (!capacity) return ''
    let scopeWord = scope === 'past' ? 'past' : 'upcoming'
    let { sessionCount, totalSessionCount } = capacity
    if (totalSessionCount === 0) return `No ${scopeWord} sessions`
    return `Combined capacity across ${sessionCount} of ${totalSessionCount} ${scopeWord} session${totalSessionCount === 1 ? '' : 's'}`
  })()

  let noSessionSelected = isSingleMode && !singleSession
  let noSessions = !noSessionSelected && capacity?.sessionCount === 0

  let emptyMsg = (() => {
    if (noSessionSelected)
      return 'Search and select a session above to view its capacity.'
    if (noSessions) {
      // The scope has sessions, just none in the selected range.
      if ((capacity?.totalSessionCount ?? 0) > 0)
        return scope === 'upcoming'
          ? 'No upcoming sessions in this range. Choose a longer range to see later sessions.'
          : 'No past sessions in this range. Choose a longer range to see earlier sessions.'
      if (scope === 'upcoming')
        return 'No upcoming sessions. Switch to Past to review completed sessions.'
      return 'No past sessions yet.'
    }
    return null
  })()

  let rsvpCounts = {
    pending: capacity?.totalPending ?? 0,
    accepted: capacity?.totalAccepted ?? 0,
    waitlisted: capacity?.totalWaitlisted ?? 0,
    declined: capacity?.totalDeclined ?? 0,
    attended: capacity?.totalAttended ?? 0,
  }

  let rows: EventLicenseCapacityRow[] = capacity?.capacityBreakdown?.length
    ? capacity.capacityBreakdown.toSorted(
        (a, b) =>
          LICENSE_ORDER.indexOf(a.licenseType) -
          LICENSE_ORDER.indexOf(b.licenseType),
      )
    : LICENSE_ORDER.map((licenseType) => ({
        licenseType,
        pending: 0,
        accepted: 0,
        waitlisted: 0,
        declined: 0,
        attended: 0,
        capacity: null,
        remaining: null,
      }))

  return (
    <Box className="flex flex-col gap-4">
      <RsvpStatCards counts={rsvpCounts} />

      {/* Header */}
      <Box className="flex items-start flex-wrap gap-4">
        <Box className="flex-1 min-w-0">
          <Typography sx={{ fontSize: 16, fontWeight: 500 }}>
            {capTitle}
          </Typography>
          {capSub && (
            <Typography
              sx={{ fontSize: 13, color: 'rgba(255,255,255,.65)', mt: '2px' }}
            >
              {capSub}
            </Typography>
          )}
        </Box>
        {isSingleMode && singleSession && capacity && (
          <Typography
            sx={{
              fontSize: 13,
              color: 'rgba(255,255,255,.6)',
              alignSelf: 'center',
            }}
          >
            Total user cap:{' '}
            <Box
              component="span"
              sx={{ color: 'rgba(255,255,255,.9)', fontWeight: 500 }}
            >
              {capacity.totalCapacity != null
                ? formatNum(capacity.totalCapacity)
                : 'Uncapped'}
            </Box>
          </Typography>
        )}
      </Box>

      {/* Scope picker */}
      <SessionScopePicker
        eventID={eventID}
        {...scopeState}
        action={
          canInvite ? (
            <Button
              variant="outlined"
              startIcon={<PersonAddIcon />}
              onClick={() => setInviteOpen(true)}
            >
              Invite Users
            </Button>
          ) : null
        }
      />

      {/* Table card */}
      <Box className="overflow-x-auto">
        <Box
          className="rounded p-6"
          sx={{
            background: '#1e2124',
            minWidth: colHeaders.length * 110,
          }}
        >
          {isLoading && !noSessionSelected ? (
            <Box className="flex justify-center py-8">
              <CircularProgress size={28} />
            </Box>
          ) : emptyMsg ? (
            <Box className="text-center py-5">
              <Typography sx={{ color: 'rgba(255,255,255,.5)', fontSize: 14 }}>
                {emptyMsg}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Column headers */}
              <Box
                className="grid gap-3 pb-3"
                sx={{
                  gridTemplateColumns: colTemplate,
                  borderBottom: '1px solid rgba(255,255,255,.12)',
                }}
              >
                {colHeaders.map((h, i) => (
                  <Typography
                    key={h}
                    className={`uppercase ${i === 0 ? '' : 'text-center'}`}
                    sx={{
                      fontSize: 12,
                      letterSpacing: '.06em',
                      color: 'rgba(255,255,255,.6)',
                    }}
                  >
                    {h}
                  </Typography>
                ))}
              </Box>

              {/* Data rows */}
              {rows.map((row) => (
                <CapacityRow
                  key={row.licenseType}
                  row={row}
                  isSingleMode={isSingleMode}
                  colTemplate={colTemplate}
                />
              ))}
            </>
          )}
        </Box>
      </Box>

      {/* Not gated on canInvite: its start-time check would unmount an open
          dialog once the session starts. The server's 409 covers that case. */}
      {singleSession && linkedServiceID && (
        <InviteUsersDialog
          open={inviteOpen}
          onClose={() => setInviteOpen(false)}
          linkedServiceID={linkedServiceID}
          session={singleSession}
          onSaved={refetchCapacity}
        />
      )}
    </Box>
  )
}

let CapacityBreakdownTabWithQueryClient = ({ eventID }: Props) => (
  <QueryClientProvider client={dashboardQueryClient}>
    <CapacityBreakdownTab eventID={eventID} />
  </QueryClientProvider>
)

export default CapacityBreakdownTabWithQueryClient
